import {ComponentPropsWithoutRef, forwardRef, HTMLAttributes, useId} from 'react';
import styled from 'styled-components';
import {interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {Typography, TypographyProps} from '../Typography';
import {ReactComponent as IconCheck} from './assets/check.svg';
import {ReactComponent as IconCircleCheck} from './assets/circle-check.svg';
import {ReactComponent as IconDash} from './assets/dash.svg';
import {ReactComponent as IconRadioOff} from './assets/radio-off.svg';
import {ReactComponent as IconRadioOn} from './assets/radio-on.svg';

interface IForm extends ComponentPropsWithoutRef<'input'> {
  variant?: 'large' | 'small';
  checkType?: 'circle' | 'check' | 'indeterminate' | 'radio';
  type?: 'checkbox';
  containerProps?: HTMLAttributes<HTMLDivElement>;
}

export const Checkbox = forwardRef<HTMLInputElement, IForm>(
  (
    {
      id: propId,
      variant = 'small',
      checkType = 'check',
      checked = false,
      disabled,
      containerProps,
      ...props
    },
    ref
  ) => {
    const uid = useId();
    const id = propId || uid;

    return (
      <Container
        variant={variant}
        onClick={(e) => e.stopPropagation()}
        {...containerProps}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          ref={ref}
          id={id}
          hidden
          {...props}
        />
        {checkType === 'circle' || checkType === 'radio' ? (
          <FakeCheckBase htmlFor={id} checked={checked} disabled={disabled}>
            {checkType === 'radio' ? (
              checked ? (
                <IconRadioOn
                  color={palette.teal100}
                  opacity={disabled ? 0.2 : 1}
                />
              ) : (
                <IconRadioOff color={disabled ? '#E4E6E7' : '#C4C8C9'} />
              )
            ) : (
              <IconCircleCheck
                color={!disabled && checked ? palette.teal100 : '#E4E6E7'}
              />
            )}
          </FakeCheckBase>
        ) : (
          <FakeCheck htmlFor={id} checked={checked} disabled={disabled}>
            {checked &&
              (checkType === 'indeterminate' ? (
                <IconDash color={disabled ? '#969B9E' : palette.white} />
              ) : (
                <IconCheck color={disabled ? '#969B9E' : palette.white} />
              ))}
          </FakeCheck>
        )}
      </Container>
    );
  }
);

const Container = styled('div').withConfig({
  shouldForwardProp: interceptProps(['variant']),
})<{
  variant: IForm['variant'];
}>`
  display: inline-block;
  ${({variant}) =>
    variant === 'small'
      ? `width: 16px;
        height: 16px;`
      : `width: 24px;
        height: 24px;`}
`;

const FakeCheckBase = styled('label').withConfig({
  shouldForwardProp: interceptProps(['disabled', 'checked']),
})<{
  disabled?: boolean;
  checked?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
  box-sizing: border-box;
  ${({disabled}) => disabled && `cursor: default;`}
`;

const FakeCheck = styled(FakeCheckBase)`
  border: solid 1px #e4e6e7;
  background-color: #e4e6e7;
  border-radius: 5px;
  &:hover {
    border-color: #b7bbbc;
  }
  ${({disabled, checked}) =>
    disabled
      ? `
    background-color: #C4C8C9;
    border-color: #C4C8C9;
    &:hover {
      border-color: #C4C8C9;
    }
    `
      : checked &&
        `
    background-color: ${palette.teal100};
    border-color: ${palette.teal100};
    &:hover {
      border-color: ${palette.teal200};
    }
  `}
`;

export const CheckboxLabel = styled(Typography).attrs(
  ({variant, color}: TypographyProps) => ({
    variant: variant ?? 'medium14',
    color: color ?? palette.textPrimary,
  })
)``;

export const CheckboxItem = styled('label').withConfig({
  shouldForwardProp: interceptProps(['disabled', 'isFlex', 'gap']),
})<{
  isFlex?: boolean;
  disabled?: boolean;
  gap?: string;
}>`
  ${({isFlex = true}) =>
    isFlex
      ? `
    display: flex;
    align-items: center;
  `
      : `
    display: inline-block;
    ${CheckboxLabel} {
      vertical-align: text-top;
    }
  `}
  ${Container} {
    margin-top: 1px;
  }
  gap: ${({gap = '4px'}) => gap};
  ${({disabled}) =>
    disabled &&
    `
    ${CheckboxLabel} {
      color: ${palette.disabled};
    }
  `}
`;
